import { useState, useRef, useEffect, useCallback } from "react"
import { ArrowUp, Loader2 } from "lucide-react"
import { useAiStore } from "@/stores/ai-store"
import { AiMentionPopover } from "./ai-mention-popover"
import { AiMentionPill } from "./ai-mention-pill"
import { cn } from "@/lib/utils"
import type { MentionEntity } from "@/lib/ag-ui-types"

interface AiComposerProps {
  onSend: (content: string, mentions: MentionEntity[]) => void
  size?: "compact" | "large"
  showContext?: boolean
  disabled?: boolean
  autoFocus?: boolean
}

export function AiComposer({
  onSend,
  size = "compact",
  showContext: _showContext = true,
  disabled = false,
  autoFocus = false,
}: AiComposerProps) {
  const [input, setInput] = useState("")
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const { isStreaming, mentionedEntities, addMention, removeMention, clearMentions } = useAiStore()

  const [mentionQuery, setMentionQuery] = useState("")
  const [showMentions, setShowMentions] = useState(false)
  const [mentionStart, setMentionStart] = useState(-1)

  const isDisabled = disabled || isStreaming
  const isLarge = size === "large"

  useEffect(() => {
    if (autoFocus) setTimeout(() => inputRef.current?.focus(), 100)
  }, [autoFocus])

  const handleSubmit = useCallback(() => {
    if (!input.trim() || isDisabled) return
    onSend(input.trim(), mentionedEntities)
    setInput("")
    clearMentions()
    setShowMentions(false)
    if (inputRef.current) inputRef.current.style.height = "auto"
  }, [input, isDisabled, onSend, mentionedEntities, clearMentions])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (showMentions) return
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value
    setInput(value)

    const cursorPos = e.target.selectionStart || 0
    const textBeforeCursor = value.slice(0, cursorPos)
    const atMatch = textBeforeCursor.match(/@(\S*)$/)

    if (atMatch) {
      setMentionQuery(atMatch[1])
      setMentionStart(cursorPos - atMatch[1].length - 1)
      setShowMentions(true)
    } else {
      setShowMentions(false)
      setMentionQuery("")
    }
  }

  const handleMentionSelect = (entity: MentionEntity) => {
    addMention(entity)
    const before = input.slice(0, mentionStart)
    const cursorPos = inputRef.current?.selectionStart || input.length
    const after = input.slice(cursorPos)
    setInput(before + after)
    setShowMentions(false)
    setMentionQuery("")
    inputRef.current?.focus()
  }

  const handleAutoGrow = (e: React.FormEvent<HTMLTextAreaElement>) => {
    const el = e.currentTarget
    el.style.height = "auto"
    const maxH = isLarge ? 180 : 96
    el.style.height = Math.min(el.scrollHeight, maxH) + "px"
  }

  const hasInput = input.trim().length > 0

  return (
    <div className="relative">
      {/* Mention popover */}
      {showMentions && (
        <AiMentionPopover
          query={mentionQuery}
          onSelect={handleMentionSelect}
          onClose={() => setShowMentions(false)}
          position={{ bottom: 56, left: 0 }}
        />
      )}

      {/* Mention pills */}
      {mentionedEntities.length > 0 && (
        <div className="mb-1.5 flex flex-wrap gap-1">
          {mentionedEntities.map((entity) => (
            <AiMentionPill
              key={entity.id}
              entity={entity}
              onRemove={() => removeMention(entity.id)}
            />
          ))}
        </div>
      )}

      {/* Input container */}
      <div
        className={cn(
          "flex items-end gap-2 border-0 bg-[var(--bg-surface)] transition-shadow",
          isLarge ? "rounded-[14px] px-5 py-3" : "rounded-[14px] px-3.5 py-2",
          "shadow-[0_0_0_1px_var(--border),0_1px_3px_rgba(0,0,0,0.02)]"
        )}
      >
        <textarea
          ref={inputRef}
          value={input}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onInput={handleAutoGrow}
          placeholder={isLarge ? "Zapytaj o cokolwiek..." : "Zapytaj AI..."}
          rows={1}
          className={cn(
            "flex-1 resize-none overflow-hidden bg-transparent text-foreground placeholder:select-none placeholder:text-[var(--text-tertiary)]",
            isLarge
              ? "min-h-[24px] max-h-[180px] text-[15px] leading-[24px]"
              : "min-h-[18px] max-h-[96px] text-[13px] leading-[18px]"
          )}
          style={{ outline: "none", border: "none", boxShadow: "none" }}
          disabled={isDisabled}
        />

        <button
          onClick={handleSubmit}
          disabled={!hasInput || isDisabled}
          className={cn(
            "flex flex-shrink-0 items-center justify-center rounded-lg transition-all",
            "h-7 w-7",
            hasInput && !isDisabled
              ? "bg-[#266DF0] text-white"
              : "text-[var(--text-tertiary)]",
            isDisabled && hasInput && "opacity-50"
          )}
        >
          {isDisabled ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <ArrowUp size={14} strokeWidth={2.5} />
          )}
        </button>
      </div>

      {isLarge && (
        <p className="mt-2 text-center text-[11px] text-[var(--text-tertiary)]">
          OkeyAI może popełniać błędy. Weryfikuj ważne informacje.
        </p>
      )}
    </div>
  )
}
